import { auth } from '../auth/firebase';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * API service class for handling all backend requests
 */
class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  /**
   * Get authorization headers with Firebase ID token
   */
  async getAuthHeaders() {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      const token = await user.getIdToken();
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
    } catch (error) {
      console.error('Error getting auth headers:', error);
      throw error;
    }
  }

  /**
   * Make authenticated API request
   */
  async request(endpoint, options = {}) {
    try {
      const url = `${this.baseURL}${endpoint}`;
      
      // Try to get auth headers, but fallback to public request if not authenticated
      let headers;
      try {
        headers = await this.getAuthHeaders();
      } catch (authError) {
        console.warn('Authentication failed, making public request:', authError);
        return this.publicRequest(endpoint, options);
      }
      
      const config = {
        headers,
        ...options
      };

      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'API request failed');
      }

      return data;
    } catch (error) {
      console.error('API request error:', error);
      throw error;
    }
  }

  /**
   * Make unauthenticated API request with fallback handling
   */
  async publicRequest(endpoint, options = {}) {
    try {
      const url = `${this.baseURL}${endpoint}`;
      
      const config = {
        headers: {
          'Content-Type': 'application/json'
        },
        ...options
      };

      const response = await fetch(url, config);
      
      // Handle network errors or server unavailable
      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
        }
        
        console.warn(`API request failed for ${endpoint}:`, errorData);
        
        // Return fallback data for critical endpoints
        return this.getFallbackData(endpoint, errorData);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.warn(`Network error for ${endpoint}:`, error.message);
      
      // Return fallback data when server is completely unavailable
      return this.getFallbackData(endpoint, error);
    }
  }

  /**
   * Get fallback data when API is unavailable
   */
  getFallbackData(endpoint, error) {
    console.warn(`Using fallback data for ${endpoint}`);
    
    // Reports endpoint fallback
    if (endpoint.includes('/reports')) {
      return {
        success: true,
        data: {
          reports: [
            {
              id: 'fallback-1',
              title: 'Sample Ocean Hazard Report',
              hazardType: 'high-waves',
              description: 'This is sample data - API server unavailable',
              severity: 'medium',
              latitude: 37.7749,
              longitude: -122.4194,
              locationName: 'San Francisco Bay',
              userId: 'demo-user',
              userName: 'Demo User',
              createdAt: new Date().toISOString(),
              status: 'pending',
              photos: []
            }
          ],
          total: 1,
          page: 1,
          limit: 50,
          totalPages: 1
        },
        message: 'Using fallback data - server unavailable'
      };
    }
    
    // Analytics endpoint fallback
    if (endpoint.includes('/analytics/dashboard')) {
      return {
        success: true,
        data: {
          overview: {
            totalReports: 127,
            activeReports: 23,
            resolvedReports: 89,
            pendingReports: 15
          },
          recentReports: [],
          severityBreakdown: {
            low: 45,
            medium: 52,
            high: 23,
            critical: 7
          }
        },
        message: 'Using fallback data - server unavailable'
      };
    }
    
    // Default fallback for other endpoints
    return {
      success: false,
      error: error.message || 'Service temporarily unavailable',
      fallback: true,
      data: null
    };
  }

  // ==================== REPORTS API ====================

  /**
   * Get all reports with filtering and pagination
   */
  async getReports(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `/reports${queryString ? `?${queryString}` : ''}`;
    return this.publicRequest(endpoint);
  }

  /**
   * Get single report by ID
   */
  async getReport(id) {
    return this.publicRequest(`/reports/${id}`);
  }

  /**
   * Create new hazard report
   */
  async createReport(reportData) {
    return this.request('/reports', {
      method: 'POST',
      body: JSON.stringify(reportData)
    });
  }

  /**
   * Update existing report
   */
  async updateReport(id, updateData) {
    return this.request(`/reports/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
  }

  /**
   * Delete report
   */
  async deleteReport(id) {
    return this.request(`/reports/${id}`, {
      method: 'DELETE'
    });
  }

  /**
   * Get current user's reports
   */
  async getUserReports() {
    return this.request('/reports/user/me');
  }

  /**
   * Upvote a report
   */
  async upvoteReport(id) {
    return this.request(`/reports/${id}/upvote`, {
      method: 'POST'
    });
  }

  /**
   * Downvote a report
   */
  async downvoteReport(id) {
    return this.request(`/reports/${id}/downvote`, {
      method: 'POST'
    });
  }

  // ==================== CONTACTS API ====================

  /**
   * Get all contacts (admin) or user's contacts
   */
  async getContacts(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `/contacts${queryString ? `?${queryString}` : ''}`;
    return this.request(endpoint);
  }

  /**
   * Get single contact by ID
   */
  async getContact(id) {
    return this.request(`/contacts/${id}`);
  }

  /**
   * Create new contact message
   */
  async createContact(contactData) {
    return this.request('/contacts', {
      method: 'POST',
      body: JSON.stringify(contactData)
    });
  }

  /**
   * Update contact (admin only)
   */
  async updateContact(id, updateData) {
    return this.request(`/contacts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
  }

  /**
   * Get current user's contacts
   */
  async getUserContacts() {
    return this.request('/contacts/user/me');
  }

  /**
   * Add response to contact (admin only)
   */
  async addContactResponse(id, responseData) {
    return this.request(`/contacts/${id}/respond`, {
      method: 'POST',
      body: JSON.stringify(responseData)
    });
  }

  // ==================== ANALYTICS API ====================

  /**
   * Get dashboard analytics
   */
  async getDashboardAnalytics() {
    return this.request('/analytics/dashboard');
  }

  /**
   * Get detailed report analytics
   */
  async getReportAnalytics(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `/analytics/reports${queryString ? `?${queryString}` : ''}`;
    return this.request(endpoint);
  }

  /**
   * Get user analytics
   */
  async getUserAnalytics() {
    return this.request('/analytics/users');
  }

  /**
   * Get performance metrics
   */
  async getPerformanceMetrics() {
    return this.request('/analytics/performance');
  }

  // ==================== AUTH API ====================

  /**
   * Verify Firebase ID token
   */
  async verifyToken(idToken) {
    return this.publicRequest('/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ idToken })
    });
  }

  /**
   * Get user profile
   */
  async getUserProfile() {
    return this.request('/auth/profile');
  }

  /**
   * Update user profile
   */
  async updateUserProfile(profileData) {
    return this.request('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData)
    });
  }

  /**
   * Logout user
   */
  async logout() {
    return this.request('/auth/logout', {
      method: 'POST'
    });
  }

  /**
   * Get user activity stats
   */
  async getUserStats() {
    return this.request('/auth/stats');
  }

  /**
   * Delete user account
   */
  async deleteAccount() {
    return this.request('/auth/account', {
      method: 'DELETE'
    });
  }
}

// Create singleton instance
const apiService = new ApiService();

export default apiService;
